import React, { FC, useCallback } from "react";

import { Button, Col, Offcanvas, OffcanvasProps, Row } from "react-bootstrap";

import classNames from "classnames";
import { ReactSVG } from "react-svg";

import { ResaleStatuses } from "@api/graphql";
import penIcon from "@assets/icons/streamline/Content-Pen-Write--Streamline-Ultimate.svg";
import { ValidationItems } from "@common/csvUtils/ResaleSchema";
import { ResaleStatusBadge } from "@components/table/ResaleStatusBadge";
import { prefix0ToSingleDigitNumber } from "@util/index";

import styles from "./CartDrawer.module.scss";

interface CartDrawerProps extends OffcanvasProps {
  contents: CartDrawerContentProps[];
  isDrawerOpen: boolean;
  onGoToSummary: () => void;
}

type CartDrawerContentStatus = "Completed" | "Draft" | "Error";

type ContentIndex = {
  index: number;
};

type CartDrawerContentFunction = {
  onEditClick: (index: number) => void;
  onDeleteClick: (index: number) => void;
};

export interface CartDrawerContentProps {
  title: string;
  owner: string;
  status: CartDrawerContentStatus;
  showEditAndDeleteButton: boolean;
  resaleStatus?: string;
  validation?: ValidationItems;
}

export const CartDrawerContent: FC<CartDrawerContentProps & ContentIndex & CartDrawerContentFunction> = ({
  index,
  title,
  owner,
  status,
  showEditAndDeleteButton,
  resaleStatus,
  validation,
  onEditClick,
  onDeleteClick,
}) => {
  const handleEditClick = useCallback(() => {
    onEditClick(index);
  }, [onEditClick, index]);

  const handleDeleteClick = useCallback(() => {
    onDeleteClick(index);
  }, [onDeleteClick, index]);

  return (
    <Row className={status === "Error" ? classNames(styles["error-card"]) : classNames(styles["drawer-content"])}>
      <div
        className={classNames(
          styles["drawer-content-index"],
          {
            [styles["error"]]: status === "Error",
          },
          "p-0",
        )}
      >
        {prefix0ToSingleDigitNumber(index)}
      </div>
      <Col sm={4}>
        <div
          className={classNames(styles["drawer-text"], {
            [styles["error"]]: status === "Error",
          })}
        >
          <b>{title}</b>
        </div>
        <div
          className={classNames(styles["drawer-text"], {
            [styles["error"]]: status === "Error",
          })}
        >
          {owner}
        </div>
        {resaleStatus && validation?.success === true ? (
          <div className="pt-2">
            <ResaleStatusBadge
              cellValue={resaleStatus as ResaleStatuses}
              resaleStatusReason={undefined}
              isExternalUser={false}
            />
          </div>
        ) : (
          <></>
        )}
      </Col>
      <Col sm={2} className={styles["drawer-content-status"]}>
        <div
          className={classNames(styles["ellipse"], {
            [styles["completed"]]: status === "Completed",
            [styles["draft"]]: status === "Draft",
            [styles["error"]]: status === "Error",
          })}
        />
        <div
          className={classNames(styles["status"], {
            [styles["completed"]]: status === "Completed",
            [styles["draft"]]: status === "Draft",
            [styles["error"]]: status === "Error",
          })}
        >
          {status}
        </div>
      </Col>
      {validation?.success === false ? (
        <div>
          {validation.issues.map((issue) => {
            const path = issue.path?.length ? (issue.path[0].key as string) : "";
            return (
              <p key={path + issue.message} className={classNames(styles["error-message"])}>
                <b>{path}</b> - {issue.message}
              </p>
            );
          })}
        </div>
      ) : (
        <></>
      )}
      {showEditAndDeleteButton && (
        <Col className={styles["drawer-content-buttons"]}>
          <Button onClick={handleEditClick} variant="none" className={styles["edit-button"]}>
            <ReactSVG src={penIcon} className={styles["icon"]} />
            Edit
          </Button>

          <Button onClick={handleDeleteClick} variant="none">
            Delete
          </Button>
        </Col>
      )}
    </Row>
  );
};

export const CartDrawer: FC<CartDrawerProps & CartDrawerContentFunction> = ({
  isDrawerOpen,
  contents,
  onDeleteClick,
  onEditClick,
  onGoToSummary,
  ...rest
}) => {
  return (
    <Offcanvas show={isDrawerOpen} placement="end" {...rest}>
      <Offcanvas.Header closeButton className={styles["drawer-header"]} />
      <Row className={styles["drawer-title"]}>
        <Col className={styles["text-box"]}>
          <Offcanvas.Title>Report resale</Offcanvas.Title>
          Select one to continue editing or submit report.
        </Col>
        <Col xs="auto">
          <Button size="lg" onClick={onGoToSummary}>
            Go to summary
          </Button>
        </Col>
      </Row>
      <Offcanvas.Body>
        {contents
          .slice()
          .map((content, index) => (
            <CartDrawerContent
              key={content.title}
              index={index + 1}
              onEditClick={onEditClick}
              onDeleteClick={onDeleteClick}
              {...content}
            />
          ))
          .reverse()}
      </Offcanvas.Body>
    </Offcanvas>
  );
};
